import { Injectable, InjectionToken, inject } from '@angular/core';

import { convertStringDatesToDateObjects } from '@fcom/core/utils/date';

import { Storage } from '../../interfaces/storage.interface';
import { WindowRef } from '../../providers/window';
import { DEFAULT_STORAGE_ROOT } from './constants';
import { environment } from '../../../../environments/environment';

export const SESSION_STORAGE_VERSION = new InjectionToken<string>('SessionStorageVersion');
export const LOCAL_STORAGE_VERSION = new InjectionToken<string>('LocalStorageVersion');

export type StorageName = 'localStorage' | 'sessionStorage';
export type StorageType = 'SESSION' | 'LOCAL';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private windowRef = inject(WindowRef);

  public static readonly STORAGE_VERSION_KEY = '__STORAGE_VERSION__';

  SESSION: Storage;
  LOCAL: Storage;

  private root: string;

  constructor() {
    const sessionStorageVersion = inject(SESSION_STORAGE_VERSION);
    const localStorageVersion = inject(LOCAL_STORAGE_VERSION);

    if (environment.storageRoot) {
      this.root = environment.storageRoot;
    } else {
      this.root = this.windowRef.nativeWindow['storageRoot'] || DEFAULT_STORAGE_ROOT;
    }
    this.SESSION = this.store('sessionStorage', sessionStorageVersion);
    this.LOCAL = this.store('localStorage', localStorageVersion);
  }

  private storageAvailable(type: StorageName): boolean {
    try {
      const storage = window[type];
      const x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return false;
    }
  }

  private store(storageName: StorageName, storageVersion: string): Storage {
    const storageAvailable = this.storageAvailable(storageName);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    const storage: Storage = {
      set(key: string, object: unknown): void {
        if (storageAvailable) {
          self.setItem(storageName, key, object);
        }
      },
      get<T>(key: string, convertDates = false): T {
        if (storageAvailable) {
          let value = self.getItem(storageName, key);
          if (value && convertDates) {
            value = convertStringDatesToDateObjects(value);
          }
          return value as T;
        }
        return undefined as T;
      },
      remove(key: string): void {
        if (storageAvailable) {
          self.removeItem(storageName, key);
        }
      },
      keys(): string[] {
        if (storageAvailable) {
          return self.keys(storageName);
        }
        return [];
      },
      clear(): void {
        this.keys().forEach(this.remove);
      },
    };

    if (storageAvailable && storageVersion && storage.get(StorageService.STORAGE_VERSION_KEY) !== storageVersion) {
      // TODO: Migrate if needed
      storage.clear();
      storage.set(StorageService.STORAGE_VERSION_KEY, storageVersion);
    }

    return storage;
  }

  private setItem(storageName: string, key: string, object: unknown): void {
    window[storageName].setItem(`${this.root}.${key}`, object);
  }

  private getItem(storageName: string, key: string): unknown {
    return window[storageName].getItem(`${this.root}.${key}`);
  }

  private removeItem(storageName: string, key: string): void {
    window[storageName].removeItem(`${this.root}.${key}`);
  }

  private keys(storageName: string): string[] {
    return Object.keys(window[storageName])
      .map((key) => key.substring(this.root.length + 1))
      .filter(Boolean);
  }
}
